import { z } from 'zod';

// ---------------------------------------------------------------------------
// Excel Format
// ---------------------------------------------------------------------------

/**
 * Supported Excel format identifiers for upload.
 * - plan_matriz: "Pautas Mantenimiento Preventivo" (13 columns, header row 6)
 * - sime: "Programacion SIME" (10 columns, header row 4)
 * - chancado: "Chancado" (10 columns, header row 4)
 */
export const ExcelFormatIdSchema = z.enum(['plan_matriz', 'sime', 'chancado']);
export type ExcelFormatId = z.infer<typeof ExcelFormatIdSchema>;

export const EXCEL_FORMAT_LABELS: Record<ExcelFormatId, string> = {
  plan_matriz: 'Plan Matriz (Pautas Mantenimiento Preventivo)',
  sime: 'SIME (Programacion SIME)',
  chancado: 'Chancado',
};

/**
 * Raw Excel row as parsed from spreadsheet (columns A-M).
 * All fields are strings/optional because Excel data is untyped.
 */
export const ExcelRowSchema = z.object({
  rowNumber: z.number().int(),
  numero: z.string().optional(),
  cliente: z.string().optional(),
  zona: z.string().optional(),
  catalogoEco: z.string().optional(),
  ticket: z.string().optional(),
  glosa: z.string().min(1, 'La glosa es obligatoria'),
  actividad: z.string().optional(),
  fechaPlanificacion: z.string().optional(),
  fechaEjecucion: z.string().optional(),
  status: z.string().optional(),
  boleta: z.string().optional(),
  sap: z.string().optional(),
  periodo: z.string().optional(),
});

export type ExcelRow = z.infer<typeof ExcelRowSchema>;

/**
 * Validated/transformed row ready for ingestion into the database.
 */
export const ProgramacionRowSchema = z.object({
  rowNumber: z.number().int(),
  numero: z.string().optional(),
  ticket: z.string().optional(),
  glosa: z.string().min(1),
  glosaNormalized: z.string().min(1),
  actividad: z.string().optional(),
  fechaPlanificacion: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/).optional(),
  fechaEjecucion: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/).optional(),
  status: z.enum(['PROGRAMADO', 'EJECUTADO', 'APROBADO', 'NO_EJECUTADO']).default('PROGRAMADO'),
  periodo: z.string().regex(/^\d{4}-\d{2}$/),
});

export type ProgramacionRow = z.infer<typeof ProgramacionRowSchema>;

/**
 * Upload result summary returned after Excel ingestion completes.
 */
export const ExcelUploadResponseSchema = z.object({
  uploadLogId: z.number().int(),
  status: z.enum(['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED']),
  totalRows: z.number().int(),
  createdRows: z.number().int(),
  updatedRows: z.number().int(),
  skippedRows: z.number().int(),
  errorRows: z.number().int(),
  unmatchedRows: z.number().int(),
  errors: z.array(z.object({
    rowNumber: z.number().int(),
    column: z.string().optional(),
    message: z.string(),
    value: z.string().optional(),
    /**
     * `'info'` marks an ADVISORY, not a failure: the row was ingested fine, it
     * just did not land in the periodo the upload form declared (SIME periods
     * run 21→20, so a weekly file whose week contains day 21 straddles the cut
     * and its later rows belong to the next period). Absent ⇒ a real error, the
     * only kind this array carried before 2026-09-15. Advisories deliberately do
     * NOT count towards `errorRows`, so the front has to read them from here.
     */
    severity: z.enum(['info', 'error']).optional(),
    /** For `severity: 'info'`: the periodo the row actually landed in. */
    periodo: z.string().optional(),
  })),
  /**
   * Top-level human-readable failure message for FAILED uploads where the
   * pipeline aborted before producing per-row errors (e.g. wrong sheet
   * name, ámbito-archivo mismatch, queue enqueue failure). Distinct from
   * `errors[]` which only carries per-row validation/ingestion failures
   * from COMPLETED runs. Frontend renders this verbatim under the failure
   * header so the operator sees the actionable cause instead of the
   * generic "Error en el proceso" label.
   */
  failureMessage: z.string().optional(),
});

export type ExcelUploadResponse = z.infer<typeof ExcelUploadResponseSchema>;

/**
 * Estado de una fila sin coincidencia. Derivado, no persistido: sale de
 * `resolvedPlanMatrizId` e `ignoredAt`, que son excluyentes por construcción
 * porque resolver una fila antes ignorada limpia el sello de ignorada.
 */
export const UnmatchedEstadoSchema = z.enum(['pendiente', 'resuelta', 'ignorada']);
export type UnmatchedEstado = z.infer<typeof UnmatchedEstadoSchema>;

/**
 * Filtro del listado, con 'todas' como cuarto valor para la pantalla que carga
 * el conjunto completo y pagina del lado del cliente.
 */
export const UnmatchedEstadoFilterSchema = z.enum([
  'pendiente',
  'resuelta',
  'ignorada',
  'todas',
]);
export type UnmatchedEstadoFilter = z.infer<typeof UnmatchedEstadoFilterSchema>;

/**
 * Query de GET /excel-ingestion/unmatched/:uploadLogId.
 *
 * `estado` por defecto 'pendiente': el listado no filtraba nada, así que una
 * fila ya resuelta reaparecía tras un F5 y el total de la paginación contaba
 * trabajo que ya no existía.
 */
export const UnmatchedQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  estado: UnmatchedEstadoFilterSchema.default('pendiente'),
});

export type UnmatchedQuery = z.infer<typeof UnmatchedQuerySchema>;

/**
 * Progreso de la resolución de un upload. Viaja en la misma respuesta que el
 * listado para que la UI muestre el avance sin una segunda llamada.
 */
export const UnmatchedCountsSchema = z.object({
  pendiente: z.number().int(),
  resuelta: z.number().int(),
  ignorada: z.number().int(),
});

export type UnmatchedCounts = z.infer<typeof UnmatchedCountsSchema>;

/**
 * De dónde salió una sugerencia. `ingest` son las dos congeladas en
 * `similarity1/2` al procesar la planilla; `fresh` las recalculadas al abrir la
 * pantalla (por eso un plan recién creado aparece sin recargar la ingesta);
 * `search` las que trajo el buscador. Viaja a la UI para poder auditar por qué
 * el sistema propuso lo que propuso.
 */
export const SuggestionOrigenSchema = z.enum(['fresh', 'ingest', 'search']);
export type SuggestionOrigen = z.infer<typeof SuggestionOrigenSchema>;

/**
 * Clasificación de cuatro estados del guard numérico. Se calcula SIEMPRE en el
 * servidor (una sola implementación, `corroborationKindOf` de `lib/glosa`) para
 * que el color que ve el operador no pueda discrepar del criterio con el que el
 * backend decide si persiste el alias.
 *
 * `no_numbers` existe por la corroboración vacía: un plan sin dígitos corrobora
 * contra cualquier glosa, así que "corrobora" no alcanza como luz verde.
 *
 * `frequency_mismatch` (2026-09-20, incidente INS-092634) separa "es otro
 * equipo" de "es el mismo equipo con otra cadencia". Antes las dos cosas se
 * reportaban como `mismatch`, así que la pantalla acusaba de tener los números
 * cambiados al único plan que sí era el equipo pedido. Como `mismatch`, NO
 * persiste alias: una cadencia no se adopta sola.
 */
export const CorroborationKindSchema = z.enum([
  'verified',
  'no_numbers',
  'mismatch',
  'frequency_mismatch',
]);

/**
 * Forma única de una sugerencia — la devuelven el listado, el buscador y el
 * plan recién creado, para que la UI las trate igual en los tres casos.
 *
 * `resolvable` es el campo que le faltaba al operador: hoy elige un plan y
 * recién al enviar le dicen "no tiene pauta asignada".
 */
export const UnmatchedSuggestionSchema = z.object({
  planMatrizId: z.number().int(),
  nombre: z.string(),
  similarity: z.number(),
  // Enriquecido el 2026-07-28. Opcionales para que un consumidor que solo lee
  // los tres campos de arriba siga compilando; el servidor los manda siempre.
  codigo: z.string().nullable().optional(),
  corroborationKind: CorroborationKindSchema.optional(),
  missingTokens: z.array(z.string()).optional(),
  resolvable: z.boolean().optional(),
  origen: SuggestionOrigenSchema.optional(),
});

export type UnmatchedSuggestion = z.infer<typeof UnmatchedSuggestionSchema>;

/**
 * Unmatched row for frontend display with fuzzy match suggestions.
 */
export const ExcelUnmatchedRowSchema = z.object({
  id: z.number().int(),
  rowNumber: z.number().int(),
  glosaOriginal: z.string(),
  glosaNormalized: z.string(),
  // Planilla provenance persisted at ingest (2026-07+): the TICKET and planned
  // date (raw dd/mm/yyyy) the resolution will honor. NULL on legacy rows.
  ticket: z.string().nullable().optional(),
  fechaPlanificacion: z.string().nullable().optional(),
  // Hasta 2026-07-28 eran exactamente las 2 congeladas al ingestar. Ahora se
  // recalculan al abrir la pantalla y se les anexan las congeladas que sobreviven,
  // así que el tope sube a 6 (5 frescas + la congelada que no repita).
  suggestions: z.array(UnmatchedSuggestionSchema).max(6),
  resolvedPlanMatrizId: z.number().int().nullable(),
  // Trazabilidad de la decisión, agregada el 2026-07-28. Opcionales para que
  // el consumidor que todavía no las lee siga compilando; el servidor las manda
  // siempre. Las fechas viajan como ISO string (JSON, no Date).
  resolvedAt: z.string().nullable().optional(),
  ignoredAt: z.string().nullable().optional(),
  ignoreReason: z.string().nullable().optional(),
  estado: UnmatchedEstadoSchema.optional(),
});

export type ExcelUnmatchedRow = z.infer<typeof ExcelUnmatchedRowSchema>;

/**
 * Request body for resolving unmatched rows (manual matching).
 *
 * Cota superior del lote: cada resolución abre su propia transacción y crea una
 * OT, así que un lote sin techo mantiene la request abierta un tiempo
 * proporcional a `resolutions.length`. 200 deja holgura de sobra sobre el peor
 * caso real observado (29 filas en la INST de julio 2026).
 */
export const ExcelConfirmMatchSchema = z.object({
  uploadLogId: z.number().int(),
  resolutions: z.array(z.object({
    unmatchedId: z.number().int(),
    planMatrizId: z.number().int().positive(),
  })).min(1).max(200),
});

export type ExcelConfirmMatch = z.infer<typeof ExcelConfirmMatchSchema>;

/**
 * Body de POST /excel-ingestion/ignore.
 *
 * Endpoint separado de /resolve porque la semántica no se parece: ignorar no
 * crea ticket, no mintea correlativo, no toca alias y no toma el advisory lock
 * — y sobre todo tiene que ser reversible. El front lo llama ANTES de /resolve
 * para que un resolve fallido no se lleve puestas las decisiones de descarte.
 */
export const ExcelIgnoreSchema = z.object({
  uploadLogId: z.number().int(),
  unmatchedIds: z.array(z.number().int().positive()).min(1).max(200),
  reason: z.string().max(500).optional(),
});

export type ExcelIgnore = z.infer<typeof ExcelIgnoreSchema>;

/** Body de POST /excel-ingestion/unignore — mismo lote, sin motivo. */
export const ExcelUnignoreSchema = z.object({
  uploadLogId: z.number().int(),
  unmatchedIds: z.array(z.number().int().positive()).min(1).max(200),
});

export type ExcelUnignore = z.infer<typeof ExcelUnignoreSchema>;

/**
 * Query de GET /excel-ingestion/plan-matriz/search.
 *
 * `uploadLogId` es obligatorio y no decorativo: el ámbito con el que se acota la
 * búsqueda es el DEL UPLOAD, no el del JWT. Un supervisor con tres ámbitos que
 * busca desde el `/plan-matriz/search` genérico ve planes de los tres, que es
 * exactamente el segundo sabor del incidente INST julio 2026.
 *
 * `unmatchedId` es opcional y solo sirve para clasificar los resultados contra
 * la glosa de esa fila (`corroborationKind` / `missingTokens`).
 */
export const ExcelPlanMatrizSearchSchema = z.object({
  uploadLogId: z.coerce.number().int().positive(),
  q: z.string().min(1).max(200),
  unmatchedId: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(15),
});

export type ExcelPlanMatrizSearch = z.infer<typeof ExcelPlanMatrizSearchSchema>;

/**
 * Body de POST /excel-ingestion/plan-matriz — alta de catálogo desde la
 * resolución, clonando la configuración de un plan "molde".
 *
 * NO lleva `ambitoId`: el servidor lo fuerza al del upload. Aceptarlo del body
 * sería reabrir el agujero cross-ámbito por la puerta de al lado.
 */
export const ExcelCreatePlanMatrizSchema = z.object({
  uploadLogId: z.number().int(),
  unmatchedId: z.number().int().positive(),
  templatePlanMatrizId: z.number().int().positive(),
  /** Por defecto la glosa normalizada de la fila (ver el handler). */
  nombre: z.string().min(1).max(500).optional(),
  codigo: z.string().min(1).max(100).optional(),
});

export type ExcelCreatePlanMatriz = z.infer<typeof ExcelCreatePlanMatrizSchema>;

/**
 * SSE progress event for real-time ingestion tracking.
 */
export const IngestionProgressSchema = z.object({
  phase: z.enum(['parsing', 'validating', 'matching', 'creating', 'saving', 'completed', 'failed']),
  current: z.number().int().min(0),
  total: z.number().int().min(0),
  message: z.string(),
  percentage: z.number().min(0).max(100),
});

export type IngestionProgress = z.infer<typeof IngestionProgressSchema>;
